/**
 * @fileoverview Tipos para features de seguridad extraídas de OpenStreetMap
 * vía Overpass API para el proyecto PTEL Andalucía.
 *
 * Fuente: Overpass API — amenity=police en Andalucía
 * Licencia: ODbL (Open Database License)
 * Coordenadas: EPSG:25830 (reproyectadas desde WGS84)
 *
 * @version 1.1.0
 * @date 2026-02-22
 */

/** Subtipos de instalación de seguridad clasificados por nombre/operator */
export type OSMSecuritySubtipo =
  | 'GUARDIA_CIVIL'
  | 'POLICIA_LOCAL'
  | 'POLICIA_NACIONAL'
  | 'OTRO';

/** Tipo de elemento OSM original */
export type OSMElementType = 'node' | 'way' | 'relation';

/**
 * Feature de seguridad normalizada para R2.
 * Esquema compatible con el patrón R2Service del proyecto.
 */
export interface OSMSecurityFeature {
  /** ID único: "osm-{type}-{id}" */
  id: string;
  /** Nombre de la instalación */
  nombre: string;
  /** Tokens pre-computados para uFuzzy (lowercase, sin acentos) */
  tokens: string;
  /** Clasificación: GUARDIA_CIVIL | POLICIA_LOCAL | POLICIA_NACIONAL | OTRO */
  subtipo: OSMSecuritySubtipo;
  /** Dirección compuesta desde tags OSM */
  direccion: string;
  /** Localidad desde tags.addr:city */
  localidad: string;
  /** Código INE municipio (5 dígitos) — asignado por proximidad centroide */
  codMun: string;
  /** Nombre municipio */
  municipio: string;
  /** Provincia */
  provincia: string;
  /** Coordenada X UTM30 EPSG:25830 */
  x: number;
  /** Coordenada Y UTM30 EPSG:25830 */
  y: number;
  /** Fuente de datos */
  fuente: 'osm';
  /** ID numérico OSM original */
  osmId: number;
  /** Tipo de elemento OSM */
  osmType: OSMElementType;
  /** Operador desde tags.operator */
  operador: string;
  /** Teléfono desde tags.phone */
  telefono: string;
}

// =============================================================================
// CONSTANTES DE VALIDACIÓN
// =============================================================================

/** Rango X válido para coordenadas OSM Security Andalucía (EPSG:25830) */
export const OSM_SEC_X_RANGE = { min: 100_000, max: 600_000 } as const;

/** Rango Y válido para coordenadas OSM Security Andalucía (EPSG:25830) */
export const OSM_SEC_Y_RANGE = { min: 3_980_000, max: 4_290_000 } as const;

/** Precisión estimada en metros (datos comunitarios OSM, ±25 m) */
export const OSM_SEC_PRECISION_M = 25;

// =============================================================================
// OPCIONES DE BÚSQUEDA Y RESULTADO
// =============================================================================

/** Opciones para búsqueda en OSMSecurityR2Service */
export interface OSMSecuritySearchOptions {
  /** Código INE del municipio (5 dígitos) */
  codINE: string;
  /** Nombre de la infraestructura a buscar (fuzzy) */
  name?: string;
  /** Filtrar por subtipo de seguridad */
  subtipo?: OSMSecuritySubtipo;
  /** Confianza mínima (0-100), default 55 */
  minConfidence?: number;
  /** Máximo de resultados, default 5 */
  maxResults?: number;
}

/** Resultado intermedio de búsqueda OSM Security */
export interface OSMSecurityResult {
  x: number;
  y: number;
  confidence: number;
  precision: number;
  source: 'osm_security_r2';
  feature: OSMSecurityFeature;
  fuzzyScore?: number;
  matchedName: string;
}
